/**
 * /reports/headcount — active people by department / designation /
 * location, and who joined or left in a date range (persona round
 * 2026-10). Data: GET /api/v1/hr-reports/headcount (HR-admin only).
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";
import { hrReportsApi, toApiError } from "@/lib/api";
import type { ReportCount, ReportPerson } from "@/lib/api/hr-reports";
import { ReportsShell } from "@/components/reports/reports-shell";
import { ReportDownloadButton } from "@/components/reports/report-download-button";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/format";
import { todayIndiaISO } from "@/lib/payroll-month";
import { fyStartISO } from "@/lib/reports";
import { useEntities } from "@/hooks/use-entities";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/reports/headcount")({
  component: HeadcountReportPage,
});

function CountTable({ title, rows }: Readonly<{ title: string; rows: ReportCount[] }>) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-0 sm:px-6">
        {rows.length === 0 ? (
          <p className="px-6 text-sm text-muted-foreground sm:px-0">Nobody active.</p>
        ) : (
          <Table>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.name}>
                  <TableCell>{r.name}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{r.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function PeopleTable({
  rows,
  dateLabel,
  dateOf,
  extra,
}: Readonly<{
  rows: ReportPerson[];
  dateLabel: string;
  dateOf: (p: ReportPerson) => string | undefined;
  extra?: (p: ReportPerson) => string;
}>) {
  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Nobody in this period.</p>;
  }
  return (
    <>
      {/* Phones: one line per person instead of a 5–6 column table. */}
      <ul className="divide-y sm:hidden">
        {rows.map((p, i) => {
          const d = dateOf(p);
          const meta = [p.employee_code, p.department, p.designation].filter(Boolean).join(" · ");
          const why = extra?.(p);
          return (
            <li key={`${p.employee_code}-${p.name}-${i}`} className="py-2.5 text-sm">
              <p className="font-medium">{p.name}</p>
              {meta && <p className="text-muted-foreground">{meta}</p>}
              <p className="text-muted-foreground">
                {dateLabel}: {d ? formatDate(d) : "—"}
              </p>
              {why && <p className="text-muted-foreground">Reason: {why}</p>}
            </li>
          );
        })}
      </ul>
      <div className="hidden overflow-x-auto sm:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Employee Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>{dateLabel}</TableHead>
              {extra && <TableHead>Reason</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p, i) => {
              const d = dateOf(p);
              return (
                <TableRow key={`${p.employee_code}-${p.name}-${i}`}>
                  <TableCell className="font-mono text-xs">{p.employee_code || "—"}</TableCell>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell>{p.department}</TableCell>
                  <TableCell>{p.designation}</TableCell>
                  <TableCell>{d ? formatDate(d) : "—"}</TableCell>
                  {extra && <TableCell>{extra(p)}</TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

function HeadcountReportPage() {
  const today = todayIndiaISO();
  const [from, setFrom] = useState(fyStartISO(today));
  const [to, setTo] = useState(today);
  const valid =
    /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && to >= from;
  const q = useQuery({
    queryKey: ["hr-reports", "headcount", from, to],
    queryFn: ({ signal }) => hrReportsApi.getHeadcount({ from, to }, signal),
    enabled: valid,
  });
  const rep = q.data;
  const entityCount = useEntities().data?.items?.length ?? 0;
  const params = { from, to };
  const hasData =
    !!rep && (rep.active_total > 0 || rep.joiners.length > 0 || rep.leavers.length > 0);

  return (
    <ReportsShell
      title="Headcount"
      description="Who works here today, and who joined or left in the period you pick."
    >
      <div className="space-y-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor="hc-from">From</Label>
            <Input id="hc-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="hc-to">To</Label>
            <Input id="hc-to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </div>
          {!valid && (
            <p className="text-sm text-destructive">The end date must be on or after the start date.</p>
          )}
        </div>

        {q.isLoading && (
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
            <Skeleton className="h-24" />
          </div>
        )}
        {q.isError && (
          <Alert variant="destructive">
            <AlertTitle>Couldn&apos;t load the report</AlertTitle>
            <AlertDescription>{toApiError(q.error).message}</AlertDescription>
          </Alert>
        )}

        {rep && !hasData && (
          <EmptyState
            icon={Users}
            title="No People Yet"
            description="Add employees under People to see headcount here."
          />
        )}

        {rep && hasData && (
          <>
            {rep.scope === "all_legal_entities" && entityCount > 1 && (
              <p className="text-sm text-muted-foreground" data-testid="headcount-scope">
                Covers all legal entities in this workspace — people aren&apos;t
                split by entity yet, so this report is the same whichever entity
                you have selected.
              </p>
            )}
            {(rep.joining_later ?? 0) > 0 && (
              <p className="text-sm text-muted-foreground" data-testid="headcount-joining-later">
                {rep.joining_later === 1
                  ? "1 person who joins later is not counted yet"
                  : `${rep.joining_later} people who join later are not counted yet`}{" "}
                — they count from their date of joining, the same as on the
                analytics Headcount card.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-3" data-testid="headcount-summary">
              {[
                { label: "Active Employees Today", value: rep.active_total },
                { label: "Joined in the Period", value: rep.joiners.length },
                { label: "Left or Leaving in the Period", value: rep.leavers.length },
              ].map((s) => (
                <Card key={s.label}>
                  <CardHeader className="pb-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</p>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-semibold tabular-nums">{s.value}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <ReportDownloadButton
                report="headcount"
                params={{ ...params, list: "summary" }}
                filename={`headcount-summary-${from}-to-${to}.csv`}
                label="Summary (CSV)"
              />
              <ReportDownloadButton
                report="headcount"
                params={{ ...params, list: "people" }}
                filename={`active-people-${to}.csv`}
                label="Active People (CSV)"
              />
              <ReportDownloadButton
                report="headcount"
                params={{ ...params, list: "joiners" }}
                filename={`joiners-${from}-to-${to}.csv`}
                label="Joiners (CSV)"
              />
              <ReportDownloadButton
                report="headcount"
                params={{ ...params, list: "leavers" }}
                filename={`leavers-${from}-to-${to}.csv`}
                label="Leavers (CSV)"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              <CountTable title="By Department" rows={rep.by_department} />
              <CountTable title="By Designation" rows={rep.by_designation} />
              <CountTable title="By Location" rows={rep.by_location} />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Joined ({rep.joiners.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <PeopleTable rows={rep.joiners} dateLabel="Date of joining" dateOf={(p) => p.joined_on} />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Left or Leaving ({rep.leavers.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <PeopleTable
                  rows={rep.leavers}
                  dateLabel="Last working day"
                  dateOf={(p) => p.last_working_day}
                  extra={(p) => `${p.reason ?? ""}${p.status ? ` · ${p.status}` : ""}`}
                />
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </ReportsShell>
  );
}
